import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { authAPI } from '../../../api/auth'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import toast from 'react-hot-toast'
import { 
  Lock,
  Eye,
  EyeOff,
  Mail
} from 'lucide-react'
import bgHeroImage from '../../../assets/images/bg-hero.jpg'
import logoImage from '../../../assets/images/logo.png'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({ email: '', password: '' })
  const { login } = useAuth()
  const navigate = useNavigate()

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      return 'Email is required'
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address'
    }
    return ''
  }

  // Password validation
  const validatePassword = (password) => {
    if (!password) {
      return 'Password is required'
    }
    if (password.length < 6) {
      return 'Password must be at least 6 characters'
    }
    return ''
  }

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }))
    }
  }

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const value = e.target.value
    setPassword(value)
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: validatePassword(value) }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Validate inputs
    const emailError = validateEmail(email)
    const passwordError = validatePassword(password)
    
    setErrors({
      email: emailError,
      password: passwordError,
    })

    // Don't proceed if there are validation errors
    if (emailError || passwordError) {
      toast.error('Please fix the errors in the form', {
        duration: 3000,
      })
      return false
    }

    setLoading(true)

    try {
      const result = await login(email, password)
      if (result.success) {
        toast.success('Login successful!', {
          duration: 3000,
        })
        // Small delay to show success message before navigation
        setTimeout(() => {
          navigate('/dashboard')
        }, 500)
        return false
      } else {
        toast.error(result.error || 'Login failed. Please check your credentials and try again.', {
          duration: 5000,
        })
        // Don't clear form data on failed login
        setLoading(false)
        return false
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Invalid credentials or an error occurred. Please try again.'
      toast.error(errorMessage, {
        duration: 5000,
      })
      // Don't clear form data on error
      setLoading(false)
      return false
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden px-4 py-12">
      {/* Oval Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-blue-500 rounded-full transform translate-x-1/4 -translate-y-1/4 animate-float-right"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-indigo-500 rounded-full transform -translate-x-1/4 translate-y-1/4 animate-float-left"></div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/50 shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center">
              <img src={logoImage} alt="LOGICLOOM Logo" className="h-16 w-auto object-contain" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <Mail size={20} />
                </div>
                <Input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setErrors(prev => ({ ...prev, email: validateEmail(email) }))}
                  required
                  autoComplete="email"
                  placeholder="merchant@example.com"
                  className={`pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1.5">
                  <span>•</span> {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                Password
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <Lock size={20} />
                </div>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => setErrors(prev => ({ ...prev, password: validatePassword(password) }))}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`pl-12 pr-12 py-3.5 bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1.5">
                  <span>•</span> {errors.password}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full mt-8 py-3.5 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200"
              loading={loading}
              disabled={!email || !password || loading}
              onClick={(e) => {
                if (loading) {
                  e.preventDefault()
                }
              }}
            >
              {!loading && (
                <>
                  <Lock className="mr-2" size={18} />
                  Sign In
                </>
              )}
              {loading && 'Signing In...'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginPage

